/**
 * The rival duellist.
 *
 * Built from primitives rather than a rigged character: a hooded robe silhouette
 * with a rim light, glowing eyes, and a wand arm that tracks its casts. The
 * design leans into the stylisation — the silhouette is what the player has to
 * read at 14 metres, and a clean shape reads better than a low-quality humanoid
 * would. Runes orbit the mage while it is chanting, which is the telegraph a
 * long incantation is supposed to pay for.
 */
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { scene as bus } from '../scene/sceneBus';
import { glowTexture, runeRingTexture } from '../assets/textures';

// Light enough to hold a silhouette against a dark hall — the brief puts the
// rival's readability above atmosphere.
const ROBE_COLOR = new THREE.Color('#4a3550');
const TRIM_COLOR = new THREE.Color('#b3414f');

export function OpponentMage() {
  const group = useRef<THREE.Group>(null);
  const body = useRef<THREE.Group>(null);
  const wandArm = useRef<THREE.Group>(null);
  const eyes = useRef<THREE.Mesh>(null);
  const aura = useRef<THREE.Sprite>(null);
  const runes = useRef<THREE.Group>(null);
  const hitFlash = useRef(0);
  const lastHp = useRef(bus.oppHp);

  const glow = useMemo(() => glowTexture(), []);
  const runeTex = useMemo(() => runeRingTexture(7), []);

  const robeMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: ROBE_COLOR,
        roughness: 0.74,
        metalness: 0.06,
        emissive: new THREE.Color('#140a18'),
        emissiveIntensity: 1,
      }),
    [],
  );
  const trimMaterial = useMemo(
    () => new THREE.MeshStandardMaterial({ color: TRIM_COLOR, roughness: 0.6, metalness: 0.2 }),
    [],
  );

  useFrame((state, delta) => {
    const g = group.current;
    if (!g) return;

    g.visible = bus.oppPresent;
    if (!bus.oppPresent) return;

    g.position.set(bus.oppX, 0, bus.oppZ);
    // Squared up to the arena axis wherever they are standing, because that is
    // the axis their aim is measured from: a mage who has sidestepped and is
    // still facing down the hall is telling you, truthfully, that their next
    // shot is going where they are looking and not where they are standing.
    g.rotation.y = bus.oppSide > 0 ? Math.PI : 0;

    const t = state.clock.elapsedTime;

    // Idle: weight shift plus breathing.
    if (body.current) {
      body.current.position.y = Math.sin(t * 1.4) * 0.035;
      body.current.rotation.z = Math.sin(t * 0.9) * 0.025;
      body.current.rotation.y = Math.sin(t * 0.6) * 0.06;
    }

    // Casting pose: the wand arm rises as the chant progresses.
    if (wandArm.current) {
      const target = bus.oppCasting ? -0.35 - bus.oppCastProgress * 0.75 : 0.15;
      wandArm.current.rotation.x += (target - wandArm.current.rotation.x) * Math.min(1, delta * 8);
      wandArm.current.rotation.z = bus.oppCasting ? Math.sin(t * 12) * 0.03 : Math.sin(t * 1.2) * 0.02;
    }

    // Hit reaction: flash and recoil when hp drops.
    if (bus.oppHp < lastHp.current - 0.01) {
      hitFlash.current = 1;
    }
    lastHp.current = bus.oppHp;
    hitFlash.current = Math.max(0, hitFlash.current - delta * 3.2);
    robeMaterial.emissive.setRGB(hitFlash.current * 0.9, hitFlash.current * 0.15, hitFlash.current * 0.2);
    if (hitFlash.current > 0 && body.current) {
      body.current.position.z = -hitFlash.current * 0.22;
    }

    // Eyes pulse; they go cold while stunned.
    if (eyes.current) {
      const mat = eyes.current.material as THREE.MeshBasicMaterial;
      const pulse = 0.6 + Math.sin(t * 3.1) * 0.15 + bus.oppCastProgress * 0.5;
      mat.color.setStyle(bus.oppStunned ? '#8fa6ff' : bus.oppCasting ? bus.oppCastColor : '#ff5f45');
      mat.opacity = bus.oppStunned ? 0.35 : pulse;
    }

    // Threat aura scales with how close the mage is to killing you.
    if (aura.current) {
      const mat = aura.current.material as THREE.SpriteMaterial;
      const strength = bus.oppCasting ? 0.35 + bus.oppCastProgress * 0.5 : 0.12;
      mat.opacity = strength;
      mat.color.setStyle(bus.oppCasting ? bus.oppCastColor : '#ff6a4a');
      aura.current.scale.setScalar(3 + bus.oppCastProgress * 2.2);
    }

    // Gathering runes: one ring per chunk of chant progress.
    if (runes.current) {
      runes.current.visible = bus.oppCasting;
      runes.current.rotation.y = t * 1.4;
      runes.current.children.forEach((child, i) => {
        const threshold = (i + 1) / runes.current!.children.length;
        const on = bus.oppCastProgress >= threshold - 0.18;
        const mesh = child as THREE.Mesh;
        const mat = mesh.material as THREE.MeshBasicMaterial;
        mat.opacity += ((on ? 0.85 : 0) - mat.opacity) * Math.min(1, delta * 6);
        mat.color.setStyle(bus.oppCastColor);
        mesh.rotation.z = t * (i % 2 === 0 ? 0.9 : -1.3);
        mesh.position.y = 1.1 + i * 0.42 + Math.sin(t * 2 + i) * 0.05;
      });
    }
  });

  return (
    <group ref={group}>
      <group ref={body}>
        {/* Robe */}
        <mesh position={[0, 0.95, 0]} castShadow material={robeMaterial}>
          <coneGeometry args={[0.72, 1.9, 14, 3, true]} />
        </mesh>
        {/* Torso */}
        <mesh position={[0, 1.72, 0]} castShadow material={robeMaterial}>
          <capsuleGeometry args={[0.29, 0.5, 4, 12]} />
        </mesh>
        {/* Shoulder mantle */}
        <mesh position={[0, 1.94, 0]} castShadow material={trimMaterial}>
          <coneGeometry args={[0.52, 0.42, 14, 1, true]} />
        </mesh>
        {/* Hood */}
        <mesh position={[0, 2.28, 0.02]} castShadow material={robeMaterial}>
          <sphereGeometry args={[0.29, 14, 12, 0, Math.PI * 2, 0, Math.PI * 0.62]} />
        </mesh>
        <mesh position={[0, 2.2, 0.06]} rotation={[0.25, 0, 0]} material={robeMaterial}>
          <coneGeometry args={[0.3, 0.42, 12, 1, true]} />
        </mesh>
        {/* Eyes inside the hood */}
        <mesh ref={eyes} position={[0, 2.18, 0.22]}>
          <planeGeometry args={[0.24, 0.06]} />
          <meshBasicMaterial color="#ff5f45" transparent opacity={0.8} toneMapped={false} />
        </mesh>

        {/* Wand arm */}
        <group ref={wandArm} position={[0.34, 1.82, 0.08]}>
          <mesh rotation={[0, 0, -0.5]} position={[0.1, -0.16, 0.12]} castShadow material={robeMaterial}>
            <capsuleGeometry args={[0.085, 0.45, 4, 8]} />
          </mesh>
          <mesh position={[0.28, -0.34, 0.4]} rotation={[1.15, 0, -0.3]} castShadow>
            <cylinderGeometry args={[0.017, 0.032, 0.78, 6]} />
            <meshStandardMaterial color="#3a2a1c" roughness={0.6} />
          </mesh>
          <sprite position={[0.35, -0.14, 0.72]} scale={[0.5, 0.5, 0.5]}>
            <spriteMaterial
              map={glow}
              color="#ff7a4a"
              transparent
              opacity={0.85}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              toneMapped={false}
            />
          </sprite>
        </group>

        {/* Off hand */}
        <mesh position={[-0.34, 1.62, 0.1]} rotation={[0, 0, 0.5]} castShadow material={robeMaterial}>
          <capsuleGeometry args={[0.085, 0.42, 4, 8]} />
        </mesh>
      </group>

      {/* Threat aura */}
      <sprite ref={aura} position={[0, 1.4, -0.15]} scale={[3, 3, 3]}>
        <spriteMaterial
          map={glow}
          color="#ff6a4a"
          transparent
          opacity={0.12}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </sprite>

      {/* Chant runes */}
      <group ref={runes} visible={false}>
        {[0, 1, 2, 3].map((i) => (
          <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[0, 1.1 + i * 0.42, 0]}>
            <planeGeometry args={[1.7 - i * 0.18, 1.7 - i * 0.18]} />
            <meshBasicMaterial
              map={runeTex}
              transparent
              opacity={0}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
        ))}
      </group>

      {/* Dedicated lighting: a key from the player's side and a hard rim from
          behind, so the rival never disappears into the hall. */}
      <pointLight position={[1.6, 3.4, 2.6]} color="#ffd7b0" intensity={16} distance={11} decay={2} />
      <pointLight position={[0, 2.6, -2.4]} color="#ff6a4a" intensity={22} distance={9} decay={2} />
      <pointLight position={[0, 0.35, 1.2]} color="#8fb0ff" intensity={6} distance={6} decay={2} />

      {/* Contact shadow stand-in so the mage is grounded even without shadows. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <circleGeometry args={[0.85, 20]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.4} depthWrite={false} />
      </mesh>
    </group>
  );
}
